import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { SizeGuideTables } from "../components/SizeGuideModal";
import SEO from "../components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AccordionItem({ question, children, isOpen, onClick }) {
  return (
    <div className="border-b border-[#3D2B1F]/10">
      <button className="w-full py-6 flex items-center justify-between text-left" onClick={onClick} data-testid={`faq-toggle-${question?.slice(0, 30)?.replace(/\s+/g, '-')?.toLowerCase()}`}>
        <h3 className="text-lg text-[#3D2B1F] pr-8">{question}</h3>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#C4A4A0] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#C4A4A0] flex-shrink-0" />}
      </button>
      {isOpen && <div className="pb-6 animate-fadeIn">{children}</div>}
    </div>
  );
}

function SizeGuideAnswer() {
  const [allGuides, setAllGuides] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const guideRes = await axios.get(`${API}/settings/size-guide`);
        const guides = { general: guideRes.data };
        const catRes = await axios.get(`${API}/settings/categories`);
        const cats = (catRes.data.categories || []).filter(c => c.enabled !== false);
        setCategories(cats);
        for (const cat of cats) {
          try {
            const res = await axios.get(`${API}/settings/size-guide?category_id=${cat.id}`);
            if (res.data && res.data.title !== guides.general?.title) guides[cat.id] = res.data;
          } catch {}
        }
        setAllGuides(guides);
        const keys = Object.keys(guides);
        if (keys.length > 0) setActiveTab(keys[0]);
      } catch {}
      finally { setLoading(false); }
    };
    loadData();
  }, []);

  if (loading) return <p className="text-[#3D2B1F]/40 text-sm py-4">Loading size guide...</p>;

  const guideKeys = Object.keys(allGuides);
  const getCategoryName = (key) => {
    if (key === "general") return "General";
    return categories.find(c => c.id === key)?.name || key;
  };

  return (
    <div>
      <p className="text-[#3D2B1F]/70 leading-relaxed font-light mb-5">
        We carry sizes XS through XL across our collection. Each piece is unique, so we recommend checking the measurements below to find your best fit.
      </p>
      {guideKeys.length > 1 && (
        <div className="flex gap-1 mb-5 border-b border-[#3D2B1F]/8">
          {guideKeys.map(key => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`px-4 py-2 text-[12px] border-b-2 transition-colors ${activeTab === key ? 'border-[#9A7B4F] text-[#9A7B4F] font-medium' : 'border-transparent text-[#3D2B1F]/40 hover:text-[#3D2B1F]/70'}`}>
              {getCategoryName(key)}
            </button>
          ))}
        </div>
      )}
      <SizeGuideTables guide={allGuides[activeTab]} />
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/faqs`);
        const all = res.data.faqs || [];
        setFaqs(all.filter(f => f.visible !== false));
      } catch {}
      setLoading(false);
    })();
  }, []);

  // Build FAQ Schema JSON-LD
  const faqJsonLd = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(f => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Everything you need to know about renting designer Indian outfits from Mahra in Melbourne. Sizing, delivery, returns & more."
        path="/faq"
        jsonLd={faqJsonLd}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "FAQ" }]}
      />
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />

      {/* Hero */}
      <section className="px-6 pt-12 pb-8 lg:px-16 lg:pt-20 lg:pb-12 text-center">
        <p className="text-[#C4A4A0] text-[11px] tracking-[0.3em] uppercase mb-3">Help Centre</p>
        <h1 className="text-4xl lg:text-5xl text-[#3D2B1F] mb-4">Frequently Asked Questions</h1>
        <p className="text-[#3D2B1F]/60 font-light max-w-xl mx-auto">Everything you need to know about borrowing from our wardrobe.</p>
      </section>

      {/* Melbourne Notice */}
      <section className="px-6 lg:px-16 mb-8">
        <div className="max-w-3xl mx-auto space-y-3">
          <div className="bg-[#B8C4B8]/20 px-6 py-4 rounded-lg text-center">
            <p className="text-[#3D2B1F] text-sm">
              <span className="font-medium">Now Launching in Melbourne!</span>
              <span className="text-[#3D2B1F]/60 ml-2">We're currently offering local pickup and $15 delivery within 15km of Oakleigh South.</span>
            </p>
          </div>
          <div className="bg-[#F5E1E0]/40 px-6 py-3 rounded-lg text-center">
            <p className="text-[#3D2B1F]/80 text-sm italic font-light">"We offer flexible rental options including full sets or individual pieces to accommodate sizing preferences."</p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="px-6 py-8 lg:px-16 lg:py-12" data-testid="faq-list">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="text-center py-12 text-[#3D2B1F]/40 text-sm">Loading...</div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12 text-[#3D2B1F]/40 text-sm">No FAQs available</div>
          ) : (
            faqs.map((faq, index) => (
              <AccordionItem
                key={faq.id || index}
                question={faq.question}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              >
                {faq.type === "size-guide" ? (
                  <SizeGuideAnswer />
                ) : (
                  <p className="text-[#3D2B1F]/70 leading-relaxed font-light whitespace-pre-line">{faq.answer}</p>
                )}
              </AccordionItem>
            ))
          )}
        </div>
      </section>

      {/* Still have questions */}
      <section className="px-6 py-12 lg:px-16 lg:py-16 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl text-[#3D2B1F] mb-4">Still have questions?</h2>
          <p className="text-[#3D2B1F]/60 font-light mb-6">We're here to help. Reach out and we'll get back to you as soon as possible.</p>
          <Link to="/contact" className="inline-block bg-[#C4A4A0] hover:bg-[#A88B87] text-white px-8 py-3 text-[11px] tracking-[0.15em] uppercase transition-colors">Contact Us</Link>
        </div>
      </section>

      {/* Link to Borrowing Terms */}
      <section className="px-6 py-8 lg:px-16 text-center">
        <p className="text-[#3D2B1F]/60 text-sm">
          For full details, read our{" "}
          <Link to="/borrowing-terms" className="text-[#C4A4A0] hover:underline">Borrowing Terms</Link>
        </p>
      </section>

      <Footer />
    </div>
  );
}
